import { toPng, getFontEmbedCSS } from "html-to-image";
import { DigitalShareItemType } from "../Enums/DigitalShareItemType";
import {
    debug,
    replaceConsole,
    restoreConsole,
} from "./Debugging/ConsoleReplacement";

interface ShareRenderJob {
    type: DigitalShareItemType;
    ref: React.MutableRefObject<HTMLDivElement>;
    dataUrl: string;
}

export interface ShareRenderJobResult {
    type: DigitalShareItemType;
    dataUrl: string;
}

export enum TemplateImageGeneratorProgressStep {
    WaitingForComponents = "WaitingForComponents",
    RenderingOutputs = "RenderingOutputs",
    Complete = "Complete",
}

export interface ITemplateImageGeneratorProgress {
    succeeded: number;
    failed: number;
    total: number;
    step: TemplateImageGeneratorProgressStep;
}

export enum RenderTargetType {
    Html,
    GoogleMap,
}

export enum GenerationFailureReason {
    TimeoutExceeded,
}

export class TemplateImageGenerator {
    shareTypes: DigitalShareItemType[];
    renderJobs: ShareRenderJob[];
    shareRefs: Map<string, React.MutableRefObject<HTMLDivElement>>;
    maxWaitMs = 0;
    startTime = 0;
    succeededImages = 0;
    failedImages = 0;
    totalImages = 0;
    canceled = false;
    onProgress: (
        succeeded: number,
        failed: number,
        total: number,
        step: TemplateImageGeneratorProgressStep
    ) => void = (
        succeeded: number,
        failed: number,
        total: number,
        step: TemplateImageGeneratorProgressStep
    ) => { };

    constructor(
        shareTypes: DigitalShareItemType[],
        shareRefs: Map<string, React.MutableRefObject<HTMLDivElement>>,
        onProgress: (
            succeeded: number,
            failed: number,
            total: number,
            step: TemplateImageGeneratorProgressStep
        ) => void = () => { }
    ) {
        this.onProgress = onProgress;
        this.shareTypes = shareTypes;
        this.shareRefs = shareRefs;
        this.renderJobs = shareTypes.map((t, i) => ({
            type: t,
            ref: null,
            dataUrl: null,
        }));
        this.totalImages = this.renderJobs.length;
    }

    private _updateProgress(success: boolean) {
        if (success) {
            this.succeededImages++;
        } else {
            this.failedImages++;
        }
        this.onProgress(
            this.succeededImages,
            this.failedImages,
            this.totalImages,
            TemplateImageGeneratorProgressStep.RenderingOutputs
        );
    }

    // Recursively calls itself until all refs are in... or until
    // the max time has elapsed.
    private _generate(
        resolve: (result: ShareRenderJobResult[]) => void,
        reject: (reason: any) => void
    ): void {
        if (this.canceled) {
            return;
        }

        this.onProgress(
            this.succeededImages,
            this.failedImages,
            this.totalImages,
            TemplateImageGeneratorProgressStep.WaitingForComponents
        );

        // If some refs aren't populated...
        if (
            this.renderJobs.find((r) => {
                const ref = this.shareRefs.get(r.type.toString());
                return ref === undefined || ref.current === null;
            })
        ) {
            const elapsed = window.performance.now() - this.startTime;
            if (elapsed >= this.maxWaitMs) {
                //...unless too much time has elapsed...
                console.log("reject due to too timeout exceeded");
                this.canceled = true;
                reject(GenerationFailureReason.TimeoutExceeded);
                return;
            }

            let delay = Math.min(100, this.maxWaitMs - elapsed);
            if (delay < 0) {
                delay = 0;
            }

            // ...keep waiting.
            setTimeout(() => {
                this._generate(resolve, reject);
            }, delay);
        } else {
            const promises: Promise<string>[] = [];

            this.onProgress(
                this.succeededImages,
                this.failedImages,
                this.totalImages,
                TemplateImageGeneratorProgressStep.RenderingOutputs
            );

            // Replacing the console here. The third-party libraries used
            // by toPng() flood the console with thousands of messages. It
            // slows the app down if dev tools is open and makes it
            // near-impossible to find our own logged messages.
            replaceConsole();

            // Kick off component -> image process for all components.
            this.renderJobs.forEach((job) => {
                // Using forEach instead of a for loop so our promise closure
                // doesn't refer to the wrong instance of job.
                promises.push(
                    new Promise<string>(
                        (
                            resolve: (value: string) => void,
                            reject: (reason: any) => void
                        ) => {
                            const ref = this.shareRefs.get(job.type.toString());

                            // We need to wait for all image tags to load
                            // before taking the snapshot.
                            const images = ref.current.querySelectorAll("img");
                            const imagePromises: Promise<void>[] = [];
                            images.forEach((image) => {
                                image.crossOrigin = "anonymous";
                                if (!image.complete) {
                                    imagePromises.push(
                                        new Promise<void>(
                                            (imageResolve: () => void, reject: () => void) => {
                                                image.onload = () => {
                                                    imageResolve();
                                                };
                                                image.onerror = () => {
                                                    // We don't have any error handling,
                                                    // so we're just continuing if the image
                                                    // tag doesn't load.
                                                    imageResolve();
                                                };
                                            }
                                        )
                                    );
                                }
                            });

                            Promise.all(imagePromises).then(async () => {
                                if (this.canceled) {
                                    return;
                                }

                                const fontEmbedCSS = await getFontEmbedCSS(ref.current);

                                toPng(ref.current, {
                                    width: 1500,
                                    height: 835,

                                    // Pixel density. Corresponds to the `scale` option in
                                    //   the html2canvas library. If we don't set this to 1,
                                    //   it will use the current device's density, which could be
                                    //   greater than 1 for high-density displays like Apple Retina,
                                    //   and will cause the 1500x835 image to be placed in the top-left
                                    //   corner of a much larger image for the final export.
                                    pixelRatio: 1,
                                    style: { margin: "0px" },
                                    cacheBust: true,
                                    fontEmbedCSS,
                                })
                                    .then((content: string) => {
                                        this._updateProgress(true);
                                        resolve(content);
                                    })
                                    .catch((reason: any) => {
                                        this.canceled = true;
                                        this._updateProgress(false);
                                        debug.log(
                                            `There was an error exporting the ${job.type} component. Reason: ${reason}`
                                        );
                                        reject(
                                            `There was an error exporting the ${job.type} component. Please contact Support.`
                                        );
                                    });
                            });
                        }
                    )
                );
            });

            // Wait until all snapshots have been created...
            Promise.all(promises)
                .then((dataUrls: string[]) => {
                    restoreConsole();
                    if (this.canceled) {
                        return;
                    }
                    // ...and then call the ready callback.
                    const results: ShareRenderJobResult[] = this.renderJobs.map(
                        (s, i) => ({
                            type: s.type,
                            dataUrl: dataUrls[i],
                        })
                    );
                    resolve(results);

                    this.shareRefs.forEach((v, k) => (v.current = null));
                    this.shareRefs.clear();
                })
                .catch((reason: any) => {
                    restoreConsole();
                    this.canceled = true;
                    reject(reason);
                });
        }
    }

    // delayMs: how long to wait before attempting to generate.
    // maxWaitMs: the maximum amount of time to spend trying to generate.
    //              IF LESS THAN delayMs, a single attempt will be made after
    //              `delayMs` has elapsed.
    generate(delayMs = 1000, maxWaitMs = 9999): Promise<ShareRenderJobResult[]> {
        return new Promise<ShareRenderJobResult[]>((resolve, reject) => {
            this.maxWaitMs = maxWaitMs;
            this.startTime = window.performance.now();
            setTimeout(() => {
                this._generate(resolve, reject);
            }, delayMs);
        });
    }
}
